/*
 * REST-action verb arithmetic for the Application area's duplicate-claim
 * resolution dialogs. A permission's ClaimValue is a CSV of verbs
 * ("GET,PUT") or the wildcard "ALL"; resolving a duplicate means combining,
 * subtracting or re-labelling those sets.
 *
 * Pure functions, no DOM: the dialogs in areas/application.ts are the only
 * caller and the unit tests exercise this file directly.
 */

/** The four verbs "ALL" expands to, in canonical CRUD order. */
export const STANDARD_VERBS = ['GET', 'POST', 'PUT', 'DELETE'] as const;

const FRIENDLY: Record<string, string> = {
    GET: 'View',
    POST: 'Create',
    PUT: 'Edit',
    DELETE: 'Delete',
    ALL: 'All',
};

/**
 * True when the raw value names the ALL wildcard. It has to be asked BEFORE
 * parseVerbSet, which expands ALL into the four standard verbs: the legacy
 * union guarded on `parseVerbSet(a).has('ALL')`, which is false by
 * construction, so combining an ALL row with anything rewrote it as the
 * explicit "GET,POST,PUT,DELETE" and quietly dropped the wildcard -- a verb
 * added to the vocabulary later would have been covered by ALL and is not
 * covered by the list.
 */
export function hasWildcard(value: string | null | undefined): boolean {
    return String(value ?? '').split(',').some(token => token.trim().toUpperCase() === 'ALL');
}

/** Parse a CSV of verbs into a set, expanding the ALL wildcard. */
export function parseVerbSet(value: string | null | undefined): Set<string> {
    const set = new Set<string>();
    for (const token of String(value ?? '').split(',')) {
        const verb = token.trim().toUpperCase();
        if (verb) set.add(verb);
    }
    if (set.has('ALL')) {
        set.delete('ALL');
        for (const verb of STANDARD_VERBS) set.add(verb);
    }
    return set;
}

/**
 * Canonical CRUD order (GET, POST, PUT, DELETE); unknown verbs sort after
 * those, alphabetically. Matches the order the seeded compound ClaimValues
 * use (Sql/Scripts/ClaimValue.sql) so a combined row lines up visually with a
 * hand-created same-verb-set row in the same grid.
 */
export function sortCanonical(verbs: readonly string[]): string[] {
    const order: readonly string[] = STANDARD_VERBS;
    return verbs.slice().sort((a, b) => {
        const ai = order.indexOf(a);
        const bi = order.indexOf(b);
        if (ai === -1 && bi === -1) return a < b ? -1 : (a > b ? 1 : 0);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
    });
}

/** Union of two verb sets. ALL on either side absorbs everything. */
export function unionVerbs(a: string | null | undefined, b: string | null | undefined): string {
    if (hasWildcard(a) || hasWildcard(b)) return 'ALL';
    const merged = new Set<string>([...parseVerbSet(a), ...parseVerbSet(b)]);
    return sortCanonical([...merged]).join(',');
}

/** `existing` minus `remove`, both expanded from ALL first. */
export function subtractVerbs(existing: string | null | undefined, remove: string | null | undefined): string {
    const from = parseVerbSet(existing);
    const drop = parseVerbSet(remove);
    return sortCanonical([...from].filter(verb => !drop.has(verb))).join(',');
}

/**
 * "GET,PUT" -> "View, Edit". Operators picked the friendly labels from a
 * dropdown, so the resolution dialogs echo those back; the raw value is what
 * still travels to the API. Unknown verbs pass through unchanged.
 */
export function friendlyVerbList(rawValue: string | null | undefined): string {
    if (!rawValue) return '';
    return String(rawValue)
        .split(',')
        .map(token => {
            const trimmed = token.trim();
            const verb = trimmed.toUpperCase();
            return verb ? (FRIENDLY[verb] ?? trimmed) : '';
        })
        .filter(label => label.length > 0)
        .join(', ');
}
